import { Transition } from '@headlessui/react'
import { CheckCircleIcon, ExclamationCircleIcon, InformationCircleIcon } from '@heroicons/react/24/outline'
import { Fragment, ReactElement } from 'react'

import { NotificationTypeEnum } from '@ui/interfaces'
// TODO: Remove this and replace with clsx
import classNames from '@ui/lib/utils/classNames'

export interface NotiticationsProps {
  show: boolean
  type: NotificationTypeEnum
  title: string
  message?: string
  richMessage?: ReactElement
  messageList?: string[]
}

/*
 * Creates an inline notification, usually to display detailed errors to the user.
 * Optional message is limited to one of the following: a plaintext message, a ReactElement, or a list of strings.
 * Ensure sizing is applied in the parent element that contains Notification.
 * @param show - Shows/hides the notification
 * @param type - Severity of notification, color scheme of notification will change based on NotificationTypeEnum input
 * @param title - Title of the notification (required)
 * @param message - Plaintext message displayed in smaller size under title (optional)
 * @param richMessage - Any ReactElement to be displayed (optional)
 * @param messageList - List of strings to be displayed in a bulleted list
 * @returns ReactElement
 */

export function Notification(props: NotiticationsProps) {
  // Tailwind JIT doesn't support dynamic classes via interpolation so this is very verbose
  const getBackgroundColor = () => {
    switch (props.type) {
      case 'warning':
        return 'bg-yellow-200'
      case 'info':
        return 'bg-gray-200'
      case 'error':
        return 'bg-red-200'
      case 'success':
      default:
        return 'bg-green-200'
    }
  }
  const getShadowRingColor = () => {
    switch (props.type) {
      case 'warning':
        return 'ring-yellow-900'
      case 'info':
        return 'ring-gray-900'
      case 'error':
        return 'ring-red-900'
      case 'success':
      default:
        return 'ring-green-900'
    }
  }
  const getIconColor = () => {
    switch (props.type) {
      case 'warning':
        return 'text-yellow-800'
      case 'info':
        return 'text-gray-800'
      case 'error':
        return 'text-red-800'
      case 'success':
      default:
        return 'text-green-800'
    }
  }

  const getIcon = () => {
    const classes = classNames(`h-6 w-6`, getIconColor())
    switch (props.type) {
      case 'warning':
        return <ExclamationCircleIcon className={classes} aria-hidden="true" />
      case 'info':
        return <InformationCircleIcon className={classes} aria-hidden="true" />
      case 'error':
        return <ExclamationCircleIcon className={classes} aria-hidden="true" />
      case 'success':
      default:
        return <CheckCircleIcon className={classes} aria-hidden="true" />
    }
  }

  const getTextColor = () => {
    switch (props.type) {
      case 'warning':
        return 'text-yellow-900'
      case 'info':
        return 'text-gray-900'
      case 'error':
        return 'text-red-900'
      case 'success':
      default:
        return 'text-green-900'
    }
  }

  return (
    <Transition
      show={props.show}
      enter="transition ease-in-out duration-600 transform"
      enterFrom="-translate-x-full"
      enterTo="translate-x-0"
      leave="transition ease-in-out duration-600 transform"
      leaveTo="translate-x-0"
      leaveFrom="-translate-x-full">
      <div
        className={classNames(
          `notification pointer-events-auto mt-6 w-full overflow-hidden rounded-lg ring-1`,
          getBackgroundColor(),
          getShadowRingColor()
        )}>
        <div className="w-full p-4">
          <div className="flex items-start">
            <div className="shrink-0">{getIcon()}</div>
            <div className="ml-3 w-0 flex-1 pt-0.5">
              <p className={classNames(`text-sm font-medium`, getTextColor())}>{props.title}</p>
              {props.message && <p className={classNames('mt-1 text-sm', getTextColor())}>{props.message}</p>}
              {props.richMessage && (
                <Fragment>
                  <div className={classNames(`mt-1 text-xs`, getTextColor())}>{props.richMessage}</div>
                </Fragment>
              )}
              {props.messageList && props.messageList?.length > 0 && (
                <Fragment>
                  <ul className={classNames(`mt-1 list-disc text-xs`, getTextColor())}>
                    {props.messageList.map((message, index) => {
                      return <li key={index}>{message}</li>
                    })}
                  </ul>
                </Fragment>
              )}
            </div>
          </div>
        </div>
      </div>
    </Transition>
  )
}
